import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { track } from '@amplitude/analytics-browser'
import { HtmlToPdf } from '@minicoursegenerator/html-to-pdf-converter'
import URLs from 'src/api/url'
import AccessRule from 'src/model/access-rule'
import UserType from 'src/model/user-types'
import { exportCourseToScormAction } from 'src/reducers/course/action'
import { useCopy } from './use-course-links'
import useStyles from './styles'

const SCORM_VERSIONS = [
  { value: '1_2', label: 'SCORM 1.2' },
  { value: '2004', label: 'SCORM 2004' },
]
// The export saga skips the callback on failure (it shows its own error).
const SCORM_TIMEOUT = 60000

const isPdfLocked = me => {
  const plan = UserType[me?.userRoleId]
  return (
    plan === 'Basic' ||
    plan === 'AppSumo (Tier 1)' ||
    UserType.isTrialEquivalent(plan)
  )
}

const upgrade = type => {
  track('limit_warning_upgrade', { from: `${type}_upgrade` })
  window.location.href = '/pricing'
}

/** Which link a delivery option uses (or that a PDF has none). */
export const LinkNote = ({ view, directUrl }) => {
  const classes = useStyles()
  const isPdf = view === 'pdf'
  return (
    <div
      className={classes.note}
      style={{ background: isPdf ? '#FFF4E5' : '#F4F6F8' }}
    >
      <span className={classes.noteIcon}>
        <Icon
          icon={isPdf ? 'tabler:file-type-pdf' : 'tabler:link'}
          width={15}
          height={15}
        />
      </span>
      <span className={classes.noteText}>
        {isPdf
          ? 'Offline copy. No link and no progress tracking.'
          : view === 'scorm'
          ? 'The SCORM package opens the direct link:'
          : 'Uses the direct link:'}
      </span>
      {!isPdf && <code className={classes.noteUrl}>{directUrl}</code>}
    </div>
  )
}

/** The course's sign-in rule comes from its learning path. */
export const AccessNote = ({ course }) => {
  const classes = useStyles()
  const history = useHistory()
  // The course page loads the path into the store.
  const path = useSelector(state => state.collection.collection)
  const pathId = course.courseCollectionId
  if (pathId && String(path?.id) !== String(pathId)) return null

  const settingsUrl = `/collection/${pathId}/settings/learner-authentication`
  return (
    <div className={classes.access}>
      Access follows the course setting:{' '}
      <strong>{AccessRule[pathId ? path.accessRule ?? 0 : 0]}</strong>.
      {pathId && (
        <>
          {' '}
          <a
            href={settingsUrl}
            onClick={event => {
              event.preventDefault()
              history.push(settingsUrl)
            }}
          >
            Change
          </a>
        </>
      )}
    </div>
  )
}

const SectionHead = ({ title, children }) => {
  const classes = useStyles()
  return (
    <div>
      <div className={classes.sectionTitle}>{title}</div>
      <div className={classes.sectionDesc}>{children}</div>
    </div>
  )
}

/** A code block and its copy button. */
const CodeCopy = ({ code, label }) => {
  const classes = useStyles()
  const [copied, copy] = useCopy()
  return (
    <>
      <pre className={classes.code}>{code}</pre>
      <button
        type="button"
        className={`${classes.darkButton} ${
          copied ? classes.copiedButton : ''
        }`}
        onClick={() => copy('code', code)}
      >
        {copied ? 'Copied' : label}
      </button>
    </>
  )
}

/** Share › Embed: viewer.js fills the sized box with the course. */
export const EmbedView = ({ course }) => {
  const classes = useStyles()
  const [width, setWidth] = useState('100%')
  const [height, setHeight] = useState('720')

  const code = [
    `<div style="width: ${width}; height: ${height}px;"><div id="mcg-form-${course.code}"></div></div>`,
    `<script src="${URLs.APP_URL}viewer.js"></script>`,
    `<script>new MiniCourse().init({ code: "${course.code}" })</script>`,
  ].join('\n')

  return (
    <div className={classes.section}>
      <SectionHead title="Embed on a website">
        Add the course as a block on any page. Learners stay on your site and
        progress is tracked as usual.
      </SectionHead>
      <div className={classes.sizeFields}>
        <label className={classes.field}>
          Width
          <input
            className={classes.input}
            value={width}
            onChange={event => setWidth(event.target.value.trim() || '100%')}
          />
        </label>
        <label className={classes.field}>
          Height (px)
          <input
            className={classes.input}
            inputMode="numeric"
            value={height}
            onChange={event =>
              setHeight(event.target.value.replace(/\D/g, '') || '720')
            }
          />
        </label>
      </div>
      <CodeCopy code={code} label="Copy embed code" />
    </div>
  )
}

/** Share › iFrame: a plain iframe of the direct link. */
export const IframeView = ({ directUrl }) => {
  const classes = useStyles()
  const code = `<iframe src="${directUrl}?embed=1" width="100%" height="720" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture" allowfullscreen></iframe>`

  return (
    <div className={classes.section}>
      <SectionHead title="iFrame code">
        A plain iframe for platforms that do not allow scripts, such as Notion,
        Webflow or most LMS pages.
      </SectionHead>
      <CodeCopy code={code} label="Copy iFrame code" />
    </div>
  )
}

/** Share › SCORM: a package for an LMS (Pro plans and up). */
export const ScormView = ({ course, me }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const timeout = useRef(null)
  const [version, setVersion] = useState(SCORM_VERSIONS[0].value)
  const [exporting, setExporting] = useState(false)
  const locked = !UserType.hasScormExport(me?.userRoleId)

  useEffect(() => {
    if (locked) track('upgrade_banner_seen', { type: 'scorm_download' })
  }, [locked])

  useEffect(() => () => clearTimeout(timeout.current), [])

  const download = () => {
    track('course_downloaded_as_scorm')
    setExporting(true)
    timeout.current = setTimeout(() => setExporting(false), SCORM_TIMEOUT)
    dispatch(
      exportCourseToScormAction(course.name, course.id, version, () => {
        clearTimeout(timeout.current)
        setExporting(false)
      }),
    )
  }

  return (
    <div className={classes.section}>
      <SectionHead title="Download as SCORM">
        Upload the package to your LMS. Completion and scores are reported back
        to the LMS.
      </SectionHead>
      {locked ? (
        <button
          type="button"
          className={classes.primaryButton}
          onClick={() => upgrade('scorm')}
        >
          <Icon icon="tabler:rocket" width={18} height={18} />
          Upgrade to Pro to export SCORM
        </button>
      ) : (
        <>
          <div className={classes.field} style={{ flex: 'none' }}>
            Version
            <div className={classes.segmented}>
              {SCORM_VERSIONS.map(item => (
                <button
                  key={item.value}
                  type="button"
                  className={`${classes.segment} ${
                    item.value === version ? classes.segmentOn : ''
                  }`}
                  onClick={() => setVersion(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className={classes.primaryButton}
            disabled={exporting}
            onClick={download}
          >
            <Icon icon="tabler:download" width={18} height={18} />
            {exporting ? 'Preparing package…' : 'Download SCORM package'}
          </button>
        </>
      )}
    </div>
  )
}

/** Share › PDF: a printable copy of every page (Plus plans and up). */
export const PdfView = ({ me }) => {
  const classes = useStyles()
  const entity = useSelector(state => state.course.entity)
  const locked = isPdfLocked(me)

  useEffect(() => {
    if (locked) track('upgrade_banner_seen', { type: 'pdf_download' })
  }, [locked])

  const html = entity?.cardDtos?.map(card => card.text || '').join('') || ''

  return (
    <div className={classes.section}>
      <SectionHead title="Download as PDF">
        A printable version of every page. Interactive blocks are exported as
        their text content.
      </SectionHead>
      {locked ? (
        <button
          type="button"
          className={classes.primaryButton}
          onClick={() => upgrade('pdf')}
        >
          <Icon icon="tabler:rocket" width={18} height={18} />
          Upgrade to Plus to download PDFs
        </button>
      ) : (
        <HtmlToPdf
          html={html}
          fontFamily="Lato"
          fontUrl="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
          fileName={`${entity?.name || 'document'}.pdf`}
        >
          {({ download, isGenerating }) => (
            <button
              type="button"
              className={classes.primaryButton}
              disabled={!entity || isGenerating}
              onClick={() => {
                track('course_downloaded_as_pdf')
                download()
              }}
            >
              <Icon icon="tabler:download" width={18} height={18} />
              {isGenerating ? 'Preparing PDF…' : 'Download PDF'}
            </button>
          )}
        </HtmlToPdf>
      )}
    </div>
  )
}
